// Requirement check: does the law require an audit, an independent review, or neither?
// The result lists its inputs, thresholds and assumptions; a person must confirm it
// before an audit engagement can be opened. The Registered Auditor still decides.
import { useEffect, useState } from 'react'
import { createAssurance, confirmAssurance, createAuditEngagement } from '../../api/kailux'
import { T } from '../../theme/theme'
import { RESULT_LABEL, BASES, auditErrorText } from '../../utils/audit'
import { Card, Field, Pill, Notice, btn, inputStyle } from './ui'

const NUM = { average_employees: '', third_party_liabilities: '', turnover: '', individuals_with_beneficial_interest: '', fiduciary_assets_peak: '' }
const FLAGS = [
  ['internally_compiled', 'The financial statements are compiled by the company\'s own staff'],
  ['owner_managed', 'Owner-managed: every holder of securities is also a director'],
  ['public_company', 'Public company'],
  ['state_linked_entity', 'State-owned or state-linked entity covered by the regulations'],
  ['moi_requires_audit', 'The memorandum of incorporation requires an audit'],
]
const tone = { audit_required: 'warn', review_required: 'info', neither_required: 'ok', not_applicable: 'muted' }

export default function AssurancePanel({ clients = [], onEngagementCreated }) {
  const [clientId, setClientId] = useState('')
  const [fy, setFy] = useState(String(new Date().getFullYear()))
  const [nums, setNums] = useState(NUM)
  const [flags, setFlags] = useState({})
  const [result, setResult] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [ack, setAck] = useState(false)
  const [basis, setBasis] = useState('required_by_law')
  const [reason, setReason] = useState('')

  useEffect(() => { if (!clientId && clients.length) setClientId(clients[0].id) }, [clients, clientId])

  const run = async (e) => {
    e.preventDefault()
    setError(''); setResult(null); setAck(false); setBusy(true)
    try {
      const body = { client_id: clientId, financial_year: fy, ...flags }
      Object.entries(nums).forEach(([k, v]) => { body[k] = k === 'individuals_with_beneficial_interest' ? parseInt(v || '0', 10) : Number(v || 0) })
      const r = await createAssurance(body)
      setResult(r)
      setBasis(r.basis_hint || 'voluntary')
    } catch (err) { setError(auditErrorText(err, 'Could not run the check.')) } finally { setBusy(false) }
  }

  const open = async () => {
    setError(''); setBusy(true)
    try {
      let a = result
      if (!a.confirmed_at) a = { ...a, ...(await confirmAssurance(a.id)) }
      const eng = await createAuditEngagement({ client_id: clientId, financial_year: fy, assessment_id: a.id, basis, basis_reason: reason || null })
      onEngagementCreated && onEngagementCreated(eng)
    } catch (err) { setError(auditErrorText(err, 'Could not open the audit engagement.')) } finally { setBusy(false) }
  }

  const needReason = basis !== 'required_by_law'
  const canOpen = result && result.result !== 'not_applicable' && ack && (!needReason || reason.trim().length >= 10) && !busy
  return (
    <Card testId="assurance-card" title="Does this client need an audit?"
      subtitle="Public Interest Score under the Companies Regulations, 2011. Enter the figures for the financial year; the result shows its workings so a Registered Auditor can check it.">
      <form onSubmit={run} data-testid="assurance-form">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: '12px' }}>
          <Field label="Client">
            <select aria-label="Client" required value={clientId} onChange={(e) => setClientId(e.target.value)} style={inputStyle}>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Financial year"><input aria-label="Financial year" required value={fy} onChange={(e) => setFy(e.target.value)} style={inputStyle} /></Field>
          <Field label="Average employees"><input aria-label="Average employees" required type="number" min="0" step="any" value={nums.average_employees} onChange={(e) => setNums({ ...nums, average_employees: e.target.value })} style={inputStyle} /></Field>
          <Field label="Third-party liabilities at year end (R)"><input aria-label="Third-party liabilities" required type="number" min="0" step="any" value={nums.third_party_liabilities} onChange={(e) => setNums({ ...nums, third_party_liabilities: e.target.value })} style={inputStyle} /></Field>
          <Field label="Turnover for the year (R)"><input aria-label="Turnover" required type="number" min="0" step="any" value={nums.turnover} onChange={(e) => setNums({ ...nums, turnover: e.target.value })} style={inputStyle} /></Field>
          <Field label="Individuals with a beneficial interest in securities"><input aria-label="Individuals with beneficial interest" required type="number" min="0" step="1" value={nums.individuals_with_beneficial_interest} onChange={(e) => setNums({ ...nums, individuals_with_beneficial_interest: e.target.value })} style={inputStyle} /></Field>
          <Field label="Highest fiduciary assets held for others (R)" hint="Leave 0 if none"><input aria-label="Fiduciary assets" type="number" min="0" step="any" value={nums.fiduciary_assets_peak} onChange={(e) => setNums({ ...nums, fiduciary_assets_peak: e.target.value })} style={inputStyle} /></Field>
        </div>
        <div style={{ marginTop: '12px', display: 'grid', gap: '6px' }}>
          {FLAGS.map(([k, label]) => (
            <label key={k} style={{ fontSize: '13px', color: T.tx, display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input type="checkbox" checked={!!flags[k]} onChange={(e) => setFlags({ ...flags, [k]: e.target.checked })} /> {label}
            </label>
          ))}
        </div>
        <div style={{ marginTop: '14px' }}><button type="submit" disabled={busy || !clientId} style={btn(true, busy || !clientId)}>{busy && !result ? 'Checking…' : 'Run the check'}</button></div>
      </form>
      {error && <div style={{ marginTop: '12px' }}><Notice tone="bad" testId="assurance-error">{error}</Notice></div>}
      {result && (
        <div data-testid="assurance-result" style={{ marginTop: '18px', borderTop: `1px solid ${T.bd}`, paddingTop: '16px' }}>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
            <Pill tone={tone[result.result]} testId="assurance-outcome">{RESULT_LABEL[result.result] || result.result}</Pill>
            {result.pis !== null && result.pis !== undefined && <span data-testid="assurance-pis" style={{ fontSize: '13px', color: T.tx }}>Public Interest Score: <strong>{result.pis}</strong></span>}
          </div>
          <ul data-testid="assurance-reasons" style={{ margin: '10px 0', paddingLeft: '18px', fontSize: '13px', color: T.tx }}>
            {(result.reasons || []).map((r) => <li key={r.code}>{r.text}</li>)}
          </ul>
          {result.thresholds_used && (
            <div style={{ fontSize: '12px', color: T.mt }} data-testid="assurance-thresholds">
              Thresholds used: audit at PIS {result.thresholds_used.pis_audit_threshold_independent?.value} (or {result.thresholds_used.pis_audit_threshold_internal?.value} if compiled internally); fiduciary assets over R{Number(result.thresholds_used.fiduciary_assets_audit_threshold?.value).toLocaleString('en-ZA')}.
            </div>
          )}
          {(result.assumptions || []).length > 0 && (
            <div style={{ marginTop: '10px' }}>
              <div style={{ fontSize: '12px', fontWeight: 800, color: T.am }}>Assumptions to check</div>
              <ul data-testid="assurance-assumptions" style={{ margin: '4px 0', paddingLeft: '18px', fontSize: '12px', color: T.lt }}>
                {result.assumptions.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            </div>
          )}
          {result.result !== 'not_applicable' && (
            <div style={{ marginTop: '14px', background: T.s2, borderRadius: '10px', padding: '14px' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: T.tx, marginBottom: '8px' }}>Open an audit engagement (uses 1 credit)</div>
              <label style={{ fontSize: '13px', color: T.tx, display: 'flex', gap: '8px', alignItems: 'flex-start', marginBottom: '10px' }}>
                <input type="checkbox" aria-label="I have reviewed" checked={ack} onChange={(e) => setAck(e.target.checked)} />
                I have reviewed the inputs, thresholds and assumptions above and confirm this result.
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: '12px' }}>
                <Field label="Basis for the audit">
                  <select aria-label="Basis for the audit" value={basis} onChange={(e) => setBasis(e.target.value)} style={inputStyle}>
                    {BASES.filter((b) => b.value !== 'required_by_law' || result.result === 'audit_required').map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
                  </select>
                </Field>
                {needReason && <Field label="Reason (at least 10 characters)"><input aria-label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} style={inputStyle} /></Field>}
              </div>
              <div style={{ marginTop: '12px' }}><button type="button" onClick={open} disabled={!canOpen} style={btn(true, !canOpen)}>Open audit engagement</button></div>
            </div>
          )}
        </div>
      )}
    </Card>
  )
}
